// Live epoch facts and their two renderings (phase 3, operator redesign):
// the COMPACT main-view row shows only the ETA tail (buildEtaSegments)
// next to the leader track; the FULL sentence (buildLiveSegments) renders
// as the first line of the blocks datastream panel. Data: /schedule (head
// position, next-slot ETA) + /summary over the running epoch's slot range
// (blocks / block fees / tips so far). Everything here is pure except the
// two DOM render helpers at the bottom; the polling lifecycle lives in
// vf-apy-chart.ts.
//
// Honesty rules (mirror the API contract §3.6 and the /health stall
// recipe):
// - The ETA is always approximate ("≈") and counts down client-side from
//   the poll-time anchor; the polled value itself is never mutated.
// - head.age_secs (plus local time since the last successful poll) beyond
//   LIVE_STALE_SECS means the schedule is stale: the countdown is
//   suppressed (a stale schedule must not tick as if live) and an amber
//   "data last observed Nm ago" segment is appended instead.
// - A failed poll never blanks rendered facts to zeros; so-far facts carry
//   forward only within the same epoch.

import type { EpochSummary, ScheduleData } from "./api";
import { lamportsToSol } from "./format";

/** Poll cadence for /schedule + /summary (contract recipe: about once a minute). */
export const LIVE_POLL_MS = 60_000;
/** Countdown re-render cadence between polls; display precision is minutes. */
export const LIVE_TICK_MS = 30_000;
/** Age beyond which the schedule is stale (contract §3.6: "> ~300 s"). */
export const LIVE_STALE_SECS = 300;
/** Remaining-ETA threshold at or below which the track cursor pulses. */
export const BLINK_ETA_SECS = 300;

/** One successful poll's facts, plus the wall-clock anchor for countdown/staleness drift. */
export interface LiveFacts {
  epoch: number;
  /** slot_index / slots_in_epoch × 100; the parser guarantees the denominator ≥ 1. */
  progressPct: number;
  /** False = /schedule served next: null (no upcoming leader slot this epoch). */
  hasNext: boolean;
  /** ETA at poll time; null when next exists but its ETA was unusable. */
  etaSecs: number | null;
  /** head.age_secs at poll time; null = unknown. */
  ageSecs: number | null;
  /** Date.now() at the successful /schedule poll — countdown and staleness anchor. */
  polledAtMs: number;
  blocks: number | null;
  feeLamports: number | null;
  tipsLamports: number | null;
}

export type LivePartKind = "label" | "value" | "stale";

export interface LivePart {
  kind: LivePartKind;
  text: string;
}

/**
 * Merges one poll's responses into facts. summary may be null (that
 * endpoint failed while /schedule succeeded); so-far facts then carry
 * forward from prev, but only within the same epoch — an epoch roll makes
 * the old totals a false statement about the new epoch, so they drop to
 * null (segments omitted) instead.
 */
export function mergeLiveFacts(
  schedule: ScheduleData,
  summary: EpochSummary | null,
  prev: LiveFacts | null,
  nowMs: number,
): LiveFacts {
  const head = schedule.head;
  const sameEpochPrev = prev !== null && prev.epoch === head.epoch ? prev : null;
  return {
    epoch: head.epoch,
    progressPct: (head.slot_index / head.slots_in_epoch) * 100,
    hasNext: schedule.next !== null,
    etaSecs: schedule.next?.eta_secs ?? null,
    ageSecs: head.age_secs,
    polledAtMs: nowMs,
    blocks: summary?.blocks ?? sameEpochPrev?.blocks ?? null,
    feeLamports: summary?.fee_reward_lamports ?? sameEpochPrev?.feeLamports ?? null,
    tipsLamports: summary?.tips_lamports ?? sameEpochPrev?.tipsLamports ?? null,
  };
}

/**
 * Seconds the polled ETA has left at nowMs, clamped at 0. The polled value
 * is never mutated; the display recomputes from the anchor each tick, so
 * ticks cannot accumulate drift. A backwards wall clock counts as zero
 * elapsed rather than extending the ETA.
 */
export function remainingEtaSecs(etaSecs: number, anchorMs: number, nowMs: number): number {
  return Math.max(0, etaSecs - Math.max(0, (nowMs - anchorMs) / 1000));
}

/**
 * Effective data age at nowMs: the daemon-reported head age (unknown → 0)
 * plus local time since the last successful poll. Failed polls therefore
 * accrue staleness even when the daemon never reported any.
 */
export function staleAgeSecs(facts: LiveFacts, nowMs: number): number {
  return (facts.ageSecs ?? 0) + Math.max(0, (nowMs - facts.polledAtMs) / 1000);
}

/**
 * ETA seconds to display text, minute precision: under a minute is worded
 * (never "0m"), ≥ 1 h is "1h 22m", else "12m". The caller prefixes "≈" —
 * every rendering of this value is approximate by contract.
 */
export function humanizeEta(secs: number): string {
  if (secs < 60) {
    return "under a minute";
  }
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h >= 1 ? `${h}h ${m}m` : `${m}m`;
}

/** Progress with one decimal, floored — never overstates how far the epoch is. */
export function formatProgressPct(pct: number): string {
  return (Math.floor(pct * 10) / 10).toFixed(1);
}

/**
 * The ETA tail — the compact main-view row's ONLY text (phase 3).
 *
 * Zero or one segment: stale data renders the amber "data last observed
 * Nm ago" (staleness stays visible in the compact row, never behind a
 * click); fresh with no upcoming slot renders "no further leader slots
 * this epoch"; fresh with a usable ETA renders "next block ≈ <eta>"; next
 * present with an unusable ETA renders nothing (neither an ETA nor a
 * no-slots claim would be a measured statement).
 */
export function buildEtaSegments(facts: LiveFacts, nowMs: number): LivePart[][] {
  const age = staleAgeSecs(facts, nowMs);
  if (age > LIVE_STALE_SECS) {
    return [[{ kind: "stale", text: `data last observed ${Math.floor(age / 60)}m ago` }]];
  }
  if (!facts.hasNext) {
    return [[{ kind: "label", text: "no further leader slots this epoch" }]];
  }
  if (facts.etaSecs !== null) {
    return [
      [
        { kind: "label", text: "next block" },
        {
          kind: "value",
          text: `≈ ${humanizeEta(remainingEtaSecs(facts.etaSecs, facts.polledAtMs, nowMs))}`,
        },
      ],
    ];
  }
  return [];
}

/**
 * True when the track cursor pulses: the remaining ETA (recomputed from
 * the anchor, like the display) is at or below BLINK_ETA_SECS and the data
 * is not stale. Stale data never blinks — a pulse asserts liveness.
 */
export function shouldBlinkCursor(facts: LiveFacts, nowMs: number): boolean {
  return (
    facts.hasNext &&
    facts.etaSecs !== null &&
    remainingEtaSecs(facts.etaSecs, facts.polledAtMs, nowMs) <= BLINK_ETA_SECS &&
    staleAgeSecs(facts, nowMs) <= LIVE_STALE_SECS
  );
}

/**
 * Assembles the full sentence's segments (renderer joins them with "·").
 *
 * Phase 3: this line renders as the FIRST line of the blocks datastream
 * panel, refreshed on the shared poll while the panel is open — the
 * compact main-view row carries only the ETA tail (buildEtaSegments).
 *
 *   this epoch · <epoch> · <progress>% · <N> blocks so far ·
 *   <X> SOL block fees so far · <Y> SOL tips so far · next block ≈ <eta>
 *
 * Variants: blocks === 0 replaces every so-far segment with "no blocks
 * produced yet this epoch" (zero blocks means zero fees and tips — one
 * measured statement instead of three); blocks === null omits the so-far
 * segments (unknown is not asserted); the tail follows buildEtaSegments
 * (stale age / no further slots / ETA / nothing).
 */
export function buildLiveSegments(facts: LiveFacts, nowMs: number): LivePart[][] {
  const segments: LivePart[][] = [
    [{ kind: "label", text: "this epoch" }],
    [{ kind: "value", text: String(facts.epoch) }],
    [{ kind: "value", text: `${formatProgressPct(facts.progressPct)}%` }],
  ];
  if (facts.blocks === 0) {
    segments.push([{ kind: "label", text: "no blocks produced yet this epoch" }]);
  } else if (facts.blocks !== null) {
    segments.push([
      { kind: "value", text: String(facts.blocks) },
      { kind: "label", text: "blocks so far" },
    ]);
    if (facts.feeLamports !== null) {
      segments.push([
        { kind: "value", text: `${lamportsToSol(facts.feeLamports)} SOL` },
        { kind: "label", text: "block fees so far" },
      ]);
    }
    if (facts.tipsLamports !== null) {
      segments.push([
        { kind: "value", text: `${lamportsToSol(facts.tipsLamports)} SOL` },
        { kind: "label", text: "tips so far" },
      ]);
    }
  }
  segments.push(...buildEtaSegments(facts, nowMs));
  return segments;
}

/** The line as plain text ("a · b · c") — test/fixture view of the segments. */
export function liveLineText(segments: LivePart[][]): string {
  return segments.map((seg) => seg.map((p) => p.text).join(" ")).join(" · ");
}

// --- DOM render helpers -------------------------------------------------------

const PART_CLASS: Record<LivePartKind, string> = {
  label: "vf-live-label",
  value: "vf-live-num vf-mono",
  stale: "vf-live-stale",
};

export function renderLiveStrip(el: HTMLElement, segments: LivePart[][]): void {
  el.replaceChildren();
  for (const [i, segment] of segments.entries()) {
    if (i > 0) {
      const sep = document.createElement("span");
      sep.className = "vf-live-sep";
      sep.setAttribute("aria-hidden", "true");
      sep.textContent = "·";
      el.append(sep);
    }
    for (const [j, part] of segment.entries()) {
      if (j > 0) {
        el.append(" ");
      }
      const span = document.createElement("span");
      span.className = PART_CLASS[part.kind];
      span.textContent = part.text;
      el.append(span);
    }
  }
}

/** First-poll failure with nothing rendered yet: muted, no error panel (ambient chrome). */
export function renderLiveUnavailable(el: HTMLElement): void {
  el.replaceChildren();
  const span = document.createElement("span");
  span.className = "vf-live-label";
  span.textContent = "live data unavailable";
  el.append(span);
}
